import { useAppDispatch, useAppSelector } from 'state/store';
import {
    getRestaurantId,
    getCurrentMenuId,
    getTableId,
    getCurrentConsumptionModeType,
    getIsFoodCourt,
    getIdempotencyKey,
    updateAppState,
} from 'state/app';
import { getAccessToken, getCustomerId, getCustomerName } from 'state/customer/customer.slice';
import {
    getOrderCart,
    getOrderComment,
    getOrderPromocode,
    getOrders,
    getOrdersTotalInclTaxWithDiscount,
} from 'state/orders';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useState } from 'react';

import { useCreateOrderMutation } from 'services/order/order.endpoints';
import { RequiredActionState, updateRequiredAction } from 'state/requiredAction';
import { getBrand, getDelayListForCreateOrder } from 'state/brand';
import { usePostMultiOrderMutation } from 'services/multi-order/multi-order.endpoint';
import { PaymentMode } from 'pages/Payment/ChoosePayment/useChoosePaymentVM';
import { getConsumptionModeList } from 'state/consumptionModeList';

import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { useWorkflowStateMachine } from 'hooks/useWorkflowStateMachine';
import { OrderErrorPayload, MultiOrderErrorPayload } from 'services/order/order.type';
import { extractErrorsCode, extractRushDelay, stringifyError } from 'utils/errors';
import { useUpdateOrder } from 'hooks/useUpdateOrder/useUpdateOrder';
import { errorsParser } from 'utils/errors/errorsParser';
import { useCartHandler } from 'hooks/useCartHandler';
import { computeCartPayload, rectifyCart } from 'utils/cart';
import { useMenuHandler } from 'hooks/useMenuHandler';
import { useCustomFlush } from 'hooks/useCustomFlush';
import {
    computeCreateMultiOrderPayload,
    computeCreateOrderPayload,
    isMainStatusValid,
    PaygreenMetadata,
} from './useCreateOrder.utils';

const MAX_RETRIES = 2;

export const useCreateOrder = () => {
    const dispatch = useAppDispatch();
    const {
        navigateToOrderError,
        navigateToTimeError,
        navigateToNetworkError,
        navigateToCartError,
        navigateToMenuNotActivatedError,
    } = useCustomNavigate();
    const { goCurrentOrder } = useWorkflowStateMachine();
    const { updateOrders } = useUpdateOrder();
    const { flushCart, flushRequiredAction, flushOrderStatuses, flushIsPaymentConfirmed } =
        useCustomFlush();

    const [savedPayload, setSavedPayload] = useState<
        | {
              paymentMethodId?: number;
              paymentMode?: PaymentMode;
              stripePaymentMethodId?: string;
          }
        | undefined
    >(undefined);
    const [retries, setRetries] = useState(0);

    const brand = useAppSelector(getBrand);
    const currentRestaurantId = useAppSelector(getRestaurantId);
    const currentMenuId = useAppSelector(getCurrentMenuId);
    const tableId = useAppSelector(getTableId);
    const customerId = useAppSelector(getCustomerId);
    const accessToken = useAppSelector(getAccessToken);
    const orders = useAppSelector(getOrders);
    const orderCart = useAppSelector(getOrderCart(currentRestaurantId));
    const orderComment = useAppSelector(getOrderComment(currentRestaurantId));
    const orderPromocode = useAppSelector(getOrderPromocode(currentRestaurantId));
    const totalInclTaxWithDiscount = useAppSelector(getOrdersTotalInclTaxWithDiscount);
    const isFoodCourt = useAppSelector(getIsFoodCourt);
    const customerName = useAppSelector(getCustomerName);
    const idempotencyKey = useAppSelector(getIdempotencyKey);

    const consumptionModeList = useAppSelector(getConsumptionModeList);
    const currentConsumptionModeType = useAppSelector(getCurrentConsumptionModeType);
    const delayList = useAppSelector(getDelayListForCreateOrder(currentConsumptionModeType));

    const setRequiredAction = useCallback(
        (payload: RequiredActionState) => dispatch(updateRequiredAction(payload)),
        [dispatch],
    );

    const { refetchMenu } = useMenuHandler();
    const {
        handlePreview,
        isLoading: isPreviewLoading,
        data: rectifiedPreviewData,
        isError: isPreviewError,
    } = useCartHandler();

    const [
        createOrderQuery,
        {
            data: orderCreated,
            isLoading: singleLoading,
            error: singleError,
            isSuccess: singleSuccess,
        },
    ] = useCreateOrderMutation();

    const [
        createMultiOrderQuery,
        {
            data: multiOrderCreated,
            isLoading: multiLoading,
            error: multiError,
            isSuccess: multiSuccess,
        },
    ] = usePostMultiOrderMutation();

    const resetIdempotencyKeyAndNavigateToOrderError = useCallback(() => {
        const errorMessage = stringifyError(singleError || multiError);

        dispatch(
            updateAppState({
                idempotencyKey: uuidv4(),
            }),
        );
        navigateToOrderError(errorMessage);
    }, [dispatch, navigateToOrderError, singleError, multiError]);

    const createOrder = async ({
        paymentMode,
        stripePaymentMethodId,
        paymentMethodId,
        edenredAccessToken,
        paygreenMetadata,
        rectifiedRushDelay,
    }: {
        paymentMode?: PaymentMode;
        stripePaymentMethodId?: string;
        paymentMethodId?: number;
        edenredAccessToken?: string;
        paygreenMetadata?: PaygreenMetadata;
        rectifiedRushDelay?: Record<number, number>;
    } = {}) => {
        flushRequiredAction();
        flushIsPaymentConfirmed();
        flushOrderStatuses();

        if (paymentMethodId || paymentMode || stripePaymentMethodId) {
            setSavedPayload({ paymentMethodId, paymentMode, stripePaymentMethodId });
        }

        if (!isFoodCourt && currentRestaurantId && currentMenuId && currentConsumptionModeType) {
            const createOrderPayload = computeCreateOrderPayload({
                orderCart,
                restaurantId: currentRestaurantId,
                currentMenuId,
                tableId,
                paymentMethodId,
                totalInclTaxWithDiscount,
                currentConsumptionModeType,
                stripePaymentMethodId,
                paygreenMetadata,
                orderComment,
                orderPromocode,
                delay: rectifiedRushDelay
                    ? rectifiedRushDelay[currentRestaurantId]
                    : delayList[currentRestaurantId],
                ...(customerId ? { customerId, userName: null } : { userName: customerName }),
            });

            await createOrderQuery({
                idempotencyKey,
                edenredAccessToken,
                body: createOrderPayload,
                ...(accessToken ? { accessToken } : {}),
            });
        }

        if (isFoodCourt && brand && currentConsumptionModeType) {
            const createMultiOrderPayload = computeCreateMultiOrderPayload({
                orders,
                consumptionModeList,
                paymentMode,
                currentConsumptionModeType,
                brandId: brand.brandId,
                delayList: rectifiedRushDelay ?? delayList,
                stripePaymentMethodId,
                tableId,
                idempotencyKey,
                ...(customerId ? { customerId, userName: null } : { userName: customerName }),
            });

            await createMultiOrderQuery({
                ...createMultiOrderPayload,
                ...(accessToken ? { accessToken } : {}),
            });
        }
    };

    useEffect(() => {
        if (singleSuccess && orderCreated?.orderUuid) {
            setRetries(0);
            if (orderCreated.requiredAction) {
                setRequiredAction({
                    ...orderCreated.requiredAction,
                    orderUuid: orderCreated.orderUuid,
                    orderId: orderCreated.orderId,
                });
            } else if (!isMainStatusValid(orderCreated.mainStatus)) {
                /*
                    For now we redirect to the OrderError page, maybe we will
                    want in the future to automatically retry the createOrder
                */
                resetIdempotencyKeyAndNavigateToOrderError();
                return;
            } else {
                dispatch(
                    updateAppState({
                        currentFamilyFilter: null,
                    }),
                );
                flushCart();
            }

            goCurrentOrder(orderCreated.orderUuid);
        }
    }, [
        orderCreated,
        singleSuccess,
        singleError,
        flushCart,
        setRequiredAction,
        goCurrentOrder,
        dispatch,
        resetIdempotencyKeyAndNavigateToOrderError,
    ]);

    useEffect(() => {
        if (multiSuccess && multiOrderCreated?.multiOrderId) {
            setRetries(0);
            const areMainStatusesNotValid =
                multiOrderCreated.multiCreatedOrders &&
                multiOrderCreated.multiCreatedOrders.filter(
                    (multiCreatedOrder) => !isMainStatusValid(multiCreatedOrder.mainStatus),
                ).length;

            if (multiOrderCreated.requiredAction) {
                setRequiredAction({
                    ...multiOrderCreated.requiredAction,
                    multiOrderId: multiOrderCreated.multiOrderId,
                    restaurantCarts: multiOrderCreated.multiCreatedOrders.map(
                        ({ orderUuid, restaurantId }) => ({ orderUuid, restaurantId }),
                    ),
                });
            } else if (
                (multiOrderCreated.mainStatus &&
                    !isMainStatusValid(multiOrderCreated.mainStatus)) ||
                areMainStatusesNotValid
            ) {
                /*
                    For now we redirect to the OrderError page, maybe we will
                    want in the future to automatically retry the createOrder
                */
                resetIdempotencyKeyAndNavigateToOrderError();
                return;
            } else {
                dispatch(
                    updateAppState({
                        currentFamilyFilter: null,
                    }),
                );
                flushCart();
            }

            goCurrentOrder(multiOrderCreated.multiOrderId);
        }
    }, [
        multiOrderCreated,
        multiSuccess,
        multiError,
        flushCart,
        setRequiredAction,
        goCurrentOrder,
        dispatch,
        resetIdempotencyKeyAndNavigateToOrderError,
    ]);

    useEffect(() => {
        if (!currentRestaurantId || !currentMenuId || !orders) return;

        if (isPreviewLoading || rectifiedPreviewData) return;

        if (multiError || singleError) {
            const genericError = (singleError || multiError)!;
            if ('status' in genericError && genericError.status === 'FETCH_ERROR') {
                navigateToNetworkError();
                return;
            }

            const error = genericError as OrderErrorPayload | MultiOrderErrorPayload;
            const rootErrorCode = error.data.code;
            const errorCodes = extractErrorsCode(error);

            if (rootErrorCode === 'stripe_card_declined') {
                resetIdempotencyKeyAndNavigateToOrderError();
            } else if (errorCodes.includes('expected_at_too_early')) {
                if (retries < MAX_RETRIES) {
                    setRetries(retries + 1);
                    // We extract the new delays and retry the order creation
                    const rectifiedRushDelay = extractRushDelay(error);
                    createOrder({ ...savedPayload, rectifiedRushDelay });
                } else {
                    setRetries(0);
                    navigateToTimeError();
                }
            } else if (errorCodes.includes('invalid_cart')) {
                const parsedErrors = errorsParser(error.data.extraData, orders);
                const rectifiedCart = rectifyCart(orderCart, parsedErrors);
                handlePreview([
                    {
                        updatedCart: computeCartPayload(rectifiedCart),
                        restaurantId: currentRestaurantId,
                        menuId: currentMenuId,
                    },
                ]);
                refetchMenu();
            } else if (errorCodes.includes('menu_not_activated')) {
                navigateToMenuNotActivatedError();
            } else if (error.status === 400) {
                navigateToOrderError(error.data.message);
            } else {
                Sentry.captureMessage(
                    `[orderErrorConfirmEndpointRefreshed] error : ${JSON.stringify(
                        error,
                    )}, errorCodes : [${errorCodes.join(', ')}]`,
                    'debug',
                );
                resetIdempotencyKeyAndNavigateToOrderError();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        savedPayload,
        multiError,
        singleError,
        orders,
        orderCart,
        currentRestaurantId,
        currentMenuId,
        handlePreview,
        refetchMenu,
        isPreviewLoading,
        rectifiedPreviewData,
        resetIdempotencyKeyAndNavigateToOrderError,
        navigateToOrderError,
        dispatch,
        retries,
        setRetries,
        navigateToNetworkError,
    ]);

    useEffect(() => {
        if (isPreviewError) {
            navigateToOrderError();
            return;
        }
        if (rectifiedPreviewData) {
            updateOrders(rectifiedPreviewData);
            navigateToCartError();
        }
    }, [
        isPreviewError,
        rectifiedPreviewData,
        navigateToOrderError,
        navigateToCartError,
        updateOrders,
    ]);

    return {
        createOrder,
        isLoading: singleLoading || multiLoading || isPreviewLoading,
        error: singleError || multiError,
        resetIdempotencyKeyAndNavigateToOrderError,
    };
};
